import { MultipleOfferAvailability } from '../../../../types/MultipleOfferAvailability';
import { Callout } from '../../../cdl/Callout/Callout';
import { Text } from '../../../cdl/Text/Text';
import { translate } from '../../../common/helpers/translate.helper';
import { OfferModel } from '../../../offer/model/OfferModel';
import { OfferState } from '../../../offer/model/OfferState';
import { getOpenRfqOffers } from '../helpers/getOpenRfqOffers';

interface RfqStatusBarProps {
    offers: OfferModel[];
    rfqId: string;
    rfqState: string;
    multipleOfferAvailability: MultipleOfferAvailability;
}

export const RfqStatusBar = ({ offers, rfqState }: RfqStatusBarProps) => {
    const isSpot = offers.some((offer) => offer.isSpot());
    const openOffers = getOpenRfqOffers(offers);
    const hasQuotedOffer = openOffers.some((offer) => offer.wasQuoted());

    if (rfqState === OfferState.ENQUIRY) {
        // Update quotes - Multiple offers exist and at least one was quoted
        if (openOffers.length > 1 && hasQuotedOffer) {
            return (
                <Callout
                    variant="attention"
                    heading={translate('statusbar.supplier.updateQuotes.label')}
                    description={translate('statusbar.supplier.updateQuotes.subtitle')}
                />
            );
        }

        // Update quote - Only one offer exists and it was quoted
        if (openOffers.length === 1 && openOffers[0].wasQuoted()) {
            return (
                <Callout
                    variant="accent"
                    heading={translate('statusbar.supplier.updateQuote.label')}
                    description={translate('statusbar.supplier.updateQuote.subtitle')}
                />
            );
        }
    }

    if (rfqState === OfferState.ENQUIRY_CANCELED) {
        return (
            <Callout
                variant="default"
                heading={translate('rfq.detail.enquiryCanceled.heading')}
                description={translate('rfq.detail.enquiryCanceled.description')}
            />
        );
    }

    if (isSpot) {
        switch (rfqState) {
            case OfferState.ENQUIRY_DECLINED:
                return !hasQuotedOffer && offers[0]?.cancelReason ? (
                    <Callout
                        variant="default"
                        heading={translate('rfq.detail.declineReasonShownToTheCustomer')}
                        description={<Text fontStyle="italic">{`"${offers[0].cancelReason}"`}</Text>}
                    />
                ) : null;
            case OfferState.ENQUIRY_EXPIRED:
                return (
                    <Callout
                        variant="default"
                        heading={translate('rfq.detail.competitorSelected.heading')}
                        description={translate('rfq.detail.competitorSelectedBeforeQuote.description')}
                    />
                );
            case OfferState.QUOTE_DECLINED:
                return (
                    <Callout
                        variant="default"
                        heading={translate('rfq.detail.competitorSelected.heading')}
                        description={translate('rfq.detail.competitorSelected.description')}
                    />
                );
            case OfferState.ORDER:
                return (
                    <Callout
                        variant="accent"
                        heading={translate('rfq.detail.orderAwaitingConfirmation.heading')}
                        description={translate('rfq.detail.orderAwaitingConfirmation.description')}
                    />
                );
        }
    }

    if (!isSpot && rfqState === OfferState.QUOTE_CANCELED) {
        return (
            <Callout
                variant="default"
                heading={translate('rfq.detail.canceledOffer.heading')}
                description={translate('rfq.detail.canceledOffer.description')}
            />
        );
    }

    if (rfqState === OfferState.ORDER) {
        return (
            <Callout
                variant="accent"
                heading={translate('rfq.detail.orderAwaitingConfirmation.heading')}
                description={translate('rfq.detail.orderAwaitingConfirmation.description')}
            />
        );
    }

    return null;
};
